import { ElementListItem } from '../types'

/** Resolvers injected by the hook so this builder stays pure and easily unit-testable. */
export interface ElementListItemResolvers {
  getElementLabel: (id: string) => string
  getCrossSectionLabel: (id: string, elementType: ElementTypes) => string | undefined
  getAssemblyName: (id: string, elementType: ElementTypes) => string | undefined
}

interface ElementRef {
  id: string
  storey?: string
}

/** Selection ids + storey for a given element type, in model order. Rips/lintels are keyed by
 *  position_guid (the id the scene and the StructuralPlanning drawer use) and carry their own
 *  storey from the API; everything else is keyed by element guid. */
const getElementsForType = (model: PlanarModel, elementType: ElementTypes): ElementRef[] => {
  switch (elementType) {
    case 'outer_walls':
    case 'inner_walls':
      return model.walls
        .filter(wall => wall.element_type === elementType)
        .map(wall => ({ id: wall.guid, storey: wall.storey }))
    case 'slabs':
      return model.slabs.map(slab => ({ id: slab.guid, storey: slab.storey }))
    case 'roof_slabs':
      return model.roof_slabs.map(roof => ({ id: roof.guid, storey: roof.storey }))
    case 'vertical_slabs':
      return model.vertical_slabs.map(slab => ({ id: slab.guid, storey: slab.storey }))
    case 'vertical_roof_slabs':
      return model.vertical_roof_slabs.map(roof => ({ id: roof.guid, storey: roof.storey }))
    case 'beams':
      return model.beams.map(beam => ({ id: beam.guid, storey: beam.storey }))
    case 'purlins':
      return model.purlins.map(purlin => ({ id: purlin.guid, storey: purlin.storey }))
    case 'columns':
      return model.columns.map(column => ({ id: column.guid, storey: column.storey }))
    case 'rips':
      return model.rips.map(rip => ({
        id: rip.position_guid,
        storey: rip.storey,
      }))
    case 'lintels':
      return model.lintels.map(lintel => ({
        id: lintel.position_guid,
        storey: lintel.storey,
      }))
    case 'slab_beams':
      return model.vertical_slabs.flatMap(slab =>
        slab.beam ? [{ id: slab.beam.guid, storey: slab.storey }] : [],
      )
    case 'roof_slab_beams':
      return model.vertical_roof_slabs.flatMap(roof =>
        roof.beam ? [{ id: roof.beam.guid, storey: roof.storey }] : [],
      )
    case 'foundation':
      return model.foundationElements.map(foundation => ({
        id: foundation.guid,
        storey: foundation.storey,
      }))
    case 'standard_rips':
      return []
    default:
      return []
  }
}

/**
 * Builds the list items per element type from the planar model. The reusable core shared by every
 * page: given the model plus label/cross-section/assembly resolvers, it returns the items keyed by
 * element type. The pane then assembles a sub-tab's list from its one or more element types.
 */
export const buildElementListItems = (
  model: PlanarModel,
  elementTypes: ElementTypes[],
  { getElementLabel, getCrossSectionLabel, getAssemblyName }: ElementListItemResolvers,
): Record<string, ElementListItem[]> => {
  return elementTypes.reduce((collector, elementType) => {
    collector[elementType] = getElementsForType(model, elementType).map(({ id, storey }) => ({
      id,
      label: getElementLabel(id),
      elementType,
      storey,
      crossSectionLabel: getCrossSectionLabel(id, elementType),
      assemblyName: getAssemblyName(id, elementType),
    }))
    return collector
  }, {} as Record<string, ElementListItem[]>)
}
